import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, updateDoc, deleteDoc, doc, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../utils/useAuth";

export default function ManagerNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("all"); // all, unread, read

  // Load notifications
  useEffect(() => {
    if (!user) return;

    console.log("Loading notifications for user.uid:", user.uid);

    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      console.log(`Found ${snap.docs.length} notifications`);
      snap.docs.forEach(d => {
        console.log("Notification:", d.id, d.data());
      });
      setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsub;
  }, [user]);

  // Mark notification as read
  async function markAsRead(notificationId) {
    try {
      await updateDoc(doc(db, "notifications", notificationId), {
        read: true,
        readAt: new Date()
      });
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  }

  // Mark all as read
  async function markAllAsRead() {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      for (const notification of unreadNotifications) {
        await updateDoc(doc(db, "notifications", notification.id), {
          read: true,
          readAt: new Date()
        });
      }
      alert("All notifications marked as read");
    } catch (err) {
      console.error("Error marking all as read:", err);
      alert("Failed to mark all as read");
    }
  }

  // Delete notification
  async function deleteNotification(notificationId) {
    if (!window.confirm("Are you sure you want to delete this notification?")) return;
    
    try {
      await deleteDoc(doc(db, "notifications", notificationId));
    } catch (err) {
      console.error("Error deleting notification:", err);
      alert("Failed to delete notification");
    }
  }

  // Filter notifications
  const filteredNotifications = notifications.filter(n => {
    if (filter === "unread") return !n.read;
    if (filter === "read") return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Notifications</h2>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold"
          >
            Mark All as Read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 rounded font-semibold ${
            filter === "all"
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          All ({notifications.length})
        </button>
        <button
          onClick={() => setFilter("unread")}
          className={`px-4 py-2 rounded font-semibold ${
            filter === "unread"
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Unread ({unreadCount})
        </button>
        <button
          onClick={() => setFilter("read")}
          className={`px-4 py-2 rounded font-semibold ${
            filter === "read"
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Read ({notifications.length - unreadCount})
        </button>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length === 0 ? (
          <div className="bg-white p-12 rounded shadow text-center text-gray-500">
            <p className="text-lg">No notifications to display</p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`relative p-4 rounded shadow border-l-4 ${
                notification.read
                  ? "bg-white border-gray-300"
                  : "bg-blue-50 border-blue-500"
              }`}
            >
              {/* Delete Button */}
              <button
                onClick={() => deleteNotification(notification.id)}
                className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl font-bold"
                title="Delete notification"
              >
                ×
              </button>

              <div className="flex justify-between items-start pr-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {!notification.read && (
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    )}
                    <h3 className="font-semibold text-lg">{notification.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-2">{notification.message}</p>
                  <p className="text-xs text-gray-500">
                    {notification.createdAt?.toDate().toLocaleString()}
                  </p>
                </div>
                
                {!notification.read && (
                  <button
                    onClick={() => markAsRead(notification.id)}
                    className="ml-4 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Mark as Read
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
